import React from "react";
import styles from "./grid.module.scss";

// Define breakpoint type for responsive design
type Breakpoint = "xs" | "sm" | "md" | "lg" | "xl";

// Array of breakpoints for iteration
const breakpoints: Breakpoint[] = ["xs", "sm", "md", "lg", "xl"];

// Type for span prop: can be a number or an object with optional base and breakpoint values
type SpanProp =
  | number
  | ({ base?: number } & Partial<Record<Breakpoint, number>>);

// Type for offset prop: can be a number or an object with breakpoint values (no base)
type OffsetProp = number | Partial<Record<Breakpoint, number>>;

// Props interface for Grid component
interface GridProps extends React.HTMLAttributes<HTMLDivElement> {
  grow?: boolean;
  gap?: number;
}

// Grid component
function Grid({
  grow,
  className,
  gap = 10,
  style,
  children,
  ...props
}: GridProps) {
  const classes = [
    styles.grid,
    grow ? styles["grid-grow"] : "",
    className,
  ].join(" ");
  // const style = { "--gap": gap + "px" } as React.CSSProperties;
  const styleProp = { ...style, "--gap": gap + "px" };
  return (
    <div className={classes} style={styleProp} {...props}>
      {children}
    </div>
  );
}

// Props interface for GridCol component
interface GridColProps extends React.HTMLAttributes<HTMLDivElement> {
  span?: SpanProp;
  offset?: OffsetProp;
}

// GridCol component
function GridCol({
  span = 12,
  offset,
  className,
  children,
  ...props
}: GridColProps) {
  const spanClasses: string[] = [];

  // Handle span prop
  if (typeof span === "number") {
    spanClasses.push(styles[`col-span-${span}`]);
  } else if (typeof span === "object") {
    spanClasses.push(styles[`col-span-${span.base || 12}`]); // Default to 12 if base is undefined
    breakpoints.forEach((bp) => {
      if (span[bp]) {
        spanClasses.push(styles[`col-span-${bp}-${span[bp]}`]);
      }
    });
  }

  const offsetClasses: string[] = [];

  // Handle offset prop
  if (typeof offset === "number") {
    offsetClasses.push(styles[`col-offset-${offset}`]);
  } else if (typeof offset === "object") {
    breakpoints.forEach((bp) => {
      if (offset[bp]) {
        offsetClasses.push(styles[`col-offset-${bp}-${offset[bp]}`]);
      }
    });
  }

  const classes = [
    styles["grid-col"],
    ...spanClasses,
    ...offsetClasses,
    className,
  ].join(" ");
  return (
    <div className={classes} {...props}>
      {children}
    </div>
  );
}

// Export components
export { Grid, GridCol };
